import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { MagnifyingGlass, EnvelopeSimple, WhatsappLogo, CheckCircle, XCircle, Prohibit } from "@phosphor-icons/react";

const ALL = "__all__";

const STATUS_STYLES = {
  terkirim: "border-emerald-300 bg-emerald-50 text-emerald-700",
  gagal: "border-red-300 bg-red-50 text-red-700",
  mocked: "border-slate-300 bg-slate-100 text-slate-600",
};
const STATUS_LABELS = { terkirim: "Terkirim", gagal: "Gagal", mocked: "Simulasi (mock)" };
const STATUS_ICONS = { terkirim: CheckCircle, gagal: XCircle, mocked: Prohibit };

const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ channel: ALL, status: ALL, q: "" });

  const load = useCallback(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== ALL) params[k] = v; });
    api.get("/notifications", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [filters]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const count = (s) => items.filter((n) => n.status === s).length;

  return (
    <div className="p-8" data-testid="notifications-page">
      <h1 className="text-2xl font-semibold tracking-tight">Riwayat Notifikasi</h1>
      <p className="text-sm text-muted-foreground mt-1 mb-6">Seluruh email dan pesan yang dikirim sistem beserta statusnya</p>

      <div className="grid grid-cols-3 gap-3 max-w-lg mb-5">
        {["terkirim", "gagal", "mocked"].map((s) => (
          <div key={s} className="border border-border bg-card px-4 py-3" data-testid={`notif-stat-${s}`}>
            <p className="text-2xl font-semibold">{count(s)}</p>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{STATUS_LABELS[s]}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="notif-filter-search" className="pl-9 w-72" placeholder="Cari penerima / subjek / isi"
            value={filters.q} onChange={(e) => setFilters((s) => ({ ...s, q: e.target.value }))} />
        </div>
        <Select value={filters.channel} onValueChange={(v) => setFilters((s) => ({ ...s, channel: v }))}>
          <SelectTrigger data-testid="notif-filter-channel" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Semua Kanal</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters((s) => ({ ...s, status: v }))}>
          <SelectTrigger data-testid="notif-filter-status" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Semua Status</SelectItem>
            <SelectItem value="terkirim">Terkirim</SelectItem>
            <SelectItem value="gagal">Gagal</SelectItem>
            <SelectItem value="mocked">Simulasi (mock)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="notifications-table">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Waktu</th>
              <th className="px-4 py-3 font-semibold">Kanal</th>
              <th className="px-4 py-3 font-semibold">Penerima</th>
              <th className="px-4 py-3 font-semibold">Subjek & Isi</th>
              <th className="px-4 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">Belum ada notifikasi</td></tr>
            )}
            {items.map((n, i) => {
              const SIcon = STATUS_ICONS[n.status] || Prohibit;
              return (
                <tr key={n.id} data-testid={`notif-row-${i}`} className="hover:bg-slate-50 transition-colors duration-150">
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">{fmtDateTime(n.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                      {n.channel === "whatsapp" ? <WhatsappLogo size={15} className="text-emerald-600" /> : <EnvelopeSimple size={15} className="text-slate-600" />}
                      {n.channel === "whatsapp" ? "WhatsApp" : "Email"}
                    </span>
                  </td>
                  <td className="px-4 py-3 max-w-[220px]"><p className="text-xs font-mono break-words">{n.to}</p></td>
                  <td className="px-4 py-3 max-w-[380px]">
                    <p className="font-medium text-[13px]">{n.subject}</p>
                    <p className="text-xs text-muted-foreground truncate">{n.message}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span data-testid={`notif-status-${i}`} className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[n.status] || STATUS_STYLES.mocked}`}>
                      <SIcon size={12} /> {STATUS_LABELS[n.status] || n.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
